class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('[data-predictive-search-input]');
    this.resultsContainer = this.querySelector('[data-predictive-search-results]');
    this.clearButton = this.querySelector('[data-search-clear]');
    this.form = this.querySelector('form');
    this.abortController = null;
    this.selectedIndex = -1;

    if (!this.input || !this.resultsContainer) return;

    this.rootUrl = this.dataset.rootUrl || '/';
    this.sectionId = this.dataset.sectionId || 'predictive-search';

    this.onInput = this.debounce(this.onInput.bind(this), 300);
    this.input.addEventListener('input', () => this.updateClearButton());
    this.input.addEventListener('input', this.onInput);

    if (this.clearButton) {
      this.clearButton.addEventListener('click', this.onClear.bind(this));
      this.updateClearButton();
    }
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('blur', this.onBlur.bind(this));
    this.input.addEventListener('keydown', this.onKeydown.bind(this));
    document.addEventListener('click', this.onDocumentClick.bind(this));
  }

  onInput() {
    const searchTerm = this.input.value.trim();

    if (!searchTerm.length) {
      this.close();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onClear(event) {
    event.preventDefault();
    this.input.value = '';
    this.close();
    this.updateClearButton();
    this.input.focus();
  }

  updateClearButton() {
    if (!this.clearButton) return;
    this.clearButton.hidden = this.input.value.length === 0;
  }

  onFocus() {
    if (this.resultsContainer.innerHTML.trim() !== '') {
      this.open();
    }
  }

  onBlur() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    }, 150);
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.close();
    }
  }

  onKeydown(event) {
    const options = this.getOptions();

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (options.length) {
          this.selectedIndex = Math.min(this.selectedIndex + 1, options.length - 1);
          this.updateSelection(options);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (options.length) {
          this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
          this.updateSelection(options);
        }
        break;
      case 'Escape':
        this.close();
        break;
      case 'Enter':
        if (this.selectedIndex >= 0 && options[this.selectedIndex]) {
          event.preventDefault();
          const link = options[this.selectedIndex].querySelector('a');
          if (link) window.location.href = link.href;
        }
        break;
    }
  }

  getOptions() {
    return Array.from(this.resultsContainer.querySelectorAll('[role="option"]'));
  }

  updateSelection(options) {
    options.forEach((option, index) => {
      const isSelected = index === this.selectedIndex;
      option.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      option.classList.toggle('bg-gray-50', isSelected);
    });
  }

  getSearchResults(searchTerm) {
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    const params = new URLSearchParams({
      q: searchTerm,
      section_id: this.sectionId,
      'resources[type]': 'product',
      'resources[limit]': '5',
      'resources[limit_scope]': 'each',
      'resources[options][fields]': 'title,product_type,variants.title,vendor,variants.sku',
    });

    const url = `${this.rootUrl}search/suggest?${params.toString()}`;

    fetch(url, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.status);
        }
        return response.text();
      })
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const section = html.querySelector(`#shopify-section-${this.sectionId}`);

        if (!section) {
          this.close();
          return;
        }

        this.resultsContainer.innerHTML = section.innerHTML;
        this.selectedIndex = -1;
        this.open();
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        this.close();
      });
  }

  open() {
    this.resultsContainer.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.resultsContainer.hidden = true;
    this.resultsContainer.innerHTML = '';
    this.input.setAttribute('aria-expanded', 'false');
    this.selectedIndex = -1;
  }

  debounce(fn, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

customElements.define('predictive-search', PredictiveSearch);
